import { useFeatureFlag } from '../useFeatureFlag'
import { FeatureFlagName } from '../../_constants/featureFlags'

export type ServiceNotificationType = 'error' | 'warning'

export type ServiceNotification = {
  message: string
  type: ServiceNotificationType
  show: boolean
}

/**
 * Custom hook to get service notification details.
 * Service notifications can be scheduled to show only within a specific time range.
 * If the start and end times are not defined, the notification is shown whenever enabled (as long as a message exists).
 *
 * @returns ServiceNotification object or null if processing fails
 */
export const useServiceNotification = (): ServiceNotification | null => {
  const featureFlagData = useFeatureFlag(FeatureFlagName.SERVICE_NOTIFICATION)

  try {
    const startTime = featureFlagData?.startTime as string
    const endTime = featureFlagData?.endTime as string
    const message = featureFlagData?.message as string
    const type = (featureFlagData?.type || 'error') as ServiceNotificationType

    let show = featureFlagData?.enabled && !!message

    // If start and end times are defined, only show service notification if current time is within the range
    if (show && startTime && endTime) {
      const now = new Date()
      show = new Date(startTime) < now && new Date(endTime) > now
    }

    return {
      message,
      type,
      show,
    }
  } catch (error) {
    console.error(`Failed to get service notification`, error)
    return null
  }
}
